import React, { Component } from 'react';

import PropTypes from 'prop-types';
import qs from 'query-string';
import { withCookies } from 'react-cookie';
import { connect } from 'react-redux';

import { theme } from 'wanderu-css';
import {
    fetchPlacePredictionsAction,
    fetchPlaceDetailsAction,
    googleDataSelectors,
} from 'wanderu-ducks-google';
import { fetchDailyMinimumPricingAction, pricingSelectors } from 'wanderu-ducks-pricing';
import {
    widgetDataSelectors,
    predictionActions,
} from 'wanderu-ducks-widget';
import {
    i18nCoordinator,
    i18nMoment,
    selectLocale,
    selectCurrency,
} from 'wanderu-i18n';
import { getCookieOptions } from 'wanderu-utils';

import localeConfig from '../../../config/widget.locales.json';
import SearchWidget from '../../components/SearchWidget/SearchWidget';
import Events from '../../components/SearchWidget/events/SearchWidgetEvents';

const hasUserEnabledCtz = (cookies) => {
    const { enabled: userEnabledCtz = false } = cookies.get(
        'user-ctz-pref',
        { doNotUpdate: true },
    ) ?? {};
    return userEnabledCtz;
};

class Widget extends Component {
    static async serverFetchData({
        i18nCoordinator: serverI18nCoordinator,
    }) {
        serverI18nCoordinator.registerTranslatableApp({
            ...localeConfig,
            translationFetchFn: (locale) => import(`../../translations/widget/${locale}.po`),
        });
    }

    static propTypes = {
        cookies: PropTypes.shape({
            get: PropTypes.func,
            remove: PropTypes.func,
            set: PropTypes.func,
        }).isRequired,
        ctzOffersDesktop: PropTypes.bool,
        ctzOffersMobile: PropTypes.bool,
        currency: PropTypes.string,
        defaultDestinationInput: PropTypes.string,
        defaultDestinationPlaceId: PropTypes.string,
        defaultOriginInput: PropTypes.string,
        defaultOriginPlaceId: PropTypes.string,
        destinationInput: PropTypes.string,
        destinationPrediction: PropTypes.arrayOf(PropTypes.shape({
            placeId: PropTypes.string,
            text: PropTypes.string,
        })),
        fetchPlaceDetails: PropTypes.func,
        fetchPrediction: PropTypes.func,
        isMini: PropTypes.bool,
        isMobile: PropTypes.bool,
        locale: PropTypes.string,
        localStorageKey: PropTypes.string,
        location: PropTypes.shape({
            search: PropTypes.string,
        }),
        originPrediction: PropTypes.arrayOf(PropTypes.shape({
            placeId: PropTypes.string,
            text: PropTypes.string,
        })),
        readFromLocalStorage: PropTypes.bool,
        reduxDestinationInput: PropTypes.string,
        reduxDestinationPlaceId: PropTypes.string,
        reduxOriginInput: PropTypes.string,
        reduxOriginPlaceId: PropTypes.string,
        searchPage: PropTypes.bool,
        setDestinationInput: PropTypes.func,
        setDestinationPlaceId: PropTypes.func,
        setOriginInput: PropTypes.func,
        setOriginPlaceId: PropTypes.func,
        useLocalStorage: PropTypes.bool,
    };

    static defaultProps = {
        isMini: false,
        isMobile: false,
        localStorageKey: 'widgetSearchHistory_v2',
        readFromLocalStorage: true,
        searchPage: false,
        useLocalStorage: true,
    };

    componentDidMount() {
        i18nCoordinator.registerTranslatableApp({
            ...localeConfig,
            translationFetchFn: (locale) => import(`../../translations/widget/${locale}.po`),
        });
        const {
            defaultDestinationInput,
            defaultDestinationPlaceId,
            defaultOriginInput,
            defaultOriginPlaceId,
            reduxDestinationInput,
            reduxDestinationPlaceId,
            reduxOriginInput,
            reduxOriginPlaceId,
            setDestinationInput,
            setDestinationPlaceId,
            setOriginInput,
            setOriginPlaceId,
        } = this.props;

        if (defaultOriginInput && defaultOriginInput !== reduxOriginInput) {
            setOriginInput(defaultOriginInput);
        }
        if (defaultOriginPlaceId && defaultOriginPlaceId !== reduxOriginPlaceId) {
            setOriginPlaceId(defaultOriginPlaceId);
        }
        if (defaultDestinationInput && defaultDestinationInput !== reduxDestinationInput) {
            setDestinationInput(defaultDestinationInput);
        }
        if (defaultDestinationPlaceId && defaultDestinationPlaceId !== reduxDestinationPlaceId) {
            setDestinationPlaceId(defaultDestinationPlaceId);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    componentWillUnmount() {
        i18nCoordinator.unregisterTranslatableApp(localeConfig.namespace);
    }

    onToggleClicktripz = (ctzChecked) => {
        const { cookies } = this.props;
        cookies.set('user-ctz-pref', {
            enabled: ctzChecked,
        }, getCookieOptions());
    };

    deployClicktripzOffering = ({ departDate, returnDate }, ctzChecked) => {
        const {
            cookies,
            destinationInput,
        } = this.props;

        const userEnabledCtz = hasUserEnabledCtz(cookies);
        const startDate = i18nMoment(departDate).format('L');
        const endDate = returnDate
            ? i18nMoment(returnDate).format('L')
            : i18nMoment(departDate).add(3, 'days').format('L');
        const isDesktop = window.matchMedia(theme.breakpointsUp('lg')).matches;
        const placementId = isDesktop
            ? '3544-1'
            : '3545-1';
        const destination = destinationInput;

        let queryMap = {
            adults: 1,
            destination,
            placementId,
            siteId: 'wanderu',
            startDate,
        };

        if (endDate) {
            queryMap = {
                ...queryMap,
                endDate,
            };
        }

        const query = qs.stringify(queryMap);

        Events.clickTripzAdTriggered(ctzChecked, userEnabledCtz, queryMap);

        window.location.assign(`https://www.clicktripz.com/x/pas?${query}`);
    };

    render() {
        const {
            currency,
            locale,
            localStorageKey,
            location,
            useLocalStorage,
            readFromLocalStorage,
            fetchPrediction,
            fetchPlaceDetails,
            originPrediction,
            destinationPrediction,
            setOriginInput,
            setDestinationInput,
            cookies,
            ...otherProps
        } = this.props;

        const ctzChecked = hasUserEnabledCtz(cookies);

        const queries = qs.parse(location?.search) || {};

        return (
            <SearchWidget
                ctzChecked={ctzChecked}
                currency={currency}
                deployClicktripzOffering={this.deployClicktripzOffering}
                destinationPrediction={destinationPrediction}
                fetchPlaceDetails={fetchPlaceDetails}
                fetchPrediction={fetchPrediction}
                localStorageKey={useLocalStorage
                    ? localStorageKey
                    : undefined}
                locale={locale}
                onToggleClicktripz={this.onToggleClicktripz}
                originPrediction={originPrediction}
                queries={queries}
                readFromLocalStorage={readFromLocalStorage}
                setDestinationInput={setDestinationInput}
                setOriginInput={setOriginInput}
                {...otherProps}
            />
        );
    }
}
const mapStateToProps = (state, ownProps) => {
    const locale = selectLocale(state);

    const reduxOriginInput = widgetDataSelectors.selectOriginInput(state);
    const originName = googleDataSelectors.selectPlaceDetails(
        state,
        widgetDataSelectors.selectOriginPlaceId(state),
    );
    const originInput = ownProps.originInput
        || reduxOriginInput;
    const reduxPassengersInput = widgetDataSelectors.selectPassengersInput(state);
    const passengersInput = ownProps.passengersInput
        || reduxPassengersInput;
    const initialOriginInput = (originName && originName.formatted_address);
    const reduxDestinationInput = widgetDataSelectors.selectDestinationInput(state);
    const destinationName = googleDataSelectors.selectPlaceDetails(
        state,
        widgetDataSelectors.selectDestinationPlaceId(state),
    );
    const destinationInput = ownProps.destinationInput
        || reduxDestinationInput;
    const initialDestinationInput = (destinationName && destinationName.formatted_address);
    const reduxDestinationPlaceId = widgetDataSelectors.selectDestinationPlaceId(state);
    const destinationTopPrediction = googleDataSelectors.selectTopPrediction(
        state,
        {
            input: destinationInput,
            locale,
        },
    );
    const destinationPlaceId = ownProps.destinationPlaceId
        || reduxDestinationPlaceId
        || (destinationTopPrediction && destinationTopPrediction.placeId)
        || ownProps.defaultDestinationPlaceId;

    const reduxOriginPlaceId = widgetDataSelectors.selectOriginPlaceId(state);
    const originTopPrediction = googleDataSelectors.selectTopPrediction(
        state,
        {
            input: originInput,
            locale,
        },
    );
    const originPlaceId = ownProps.originPlaceId
        || reduxOriginPlaceId
        || (originTopPrediction && originTopPrediction.placeId)
        || ownProps.defaultOriginPlaceId;

    const departPricing = pricingSelectors.selectDailyDepartMinimumPricing(
        state,
        originPlaceId,
        destinationPlaceId,
    );
    const returnPricing = pricingSelectors.selectDailyReturnMinimumPricing(
        state,
        originPlaceId,
        destinationPlaceId,
    );

    return {
        currency: selectCurrency(state),
        defaultDestinationInput: ownProps.defaultDestinationInput,
        defaultDestinationPlaceId: ownProps.defaultDestinationPlaceId,
        defaultOriginInput: ownProps.defaultOriginInput,
        defaultOriginPlaceId: ownProps.defaultOriginPlaceId,
        departPricing,
        destinationInput,
        destinationPlaceId,
        destinationPrediction: googleDataSelectors
            .selectPredictions(state, { input: destinationInput, locale }),
        destinationTopPrediction,
        initialDestinationInput,
        initialOriginInput,
        locale,
        originInput,
        originPlaceId,
        originPrediction: googleDataSelectors
            .selectPredictions(state, { input: originInput, locale }),
        passengersInput,
        reduxDestinationInput,
        reduxDestinationPlaceId,
        reduxOriginInput,
        reduxOriginPlaceId,
        reduxPassengersInput,
        returnPricing,
    };
};

const mapDispatchToProps = {
    fetchDailyMinimumPricing: fetchDailyMinimumPricingAction.fetch,
    fetchPlaceDetails: fetchPlaceDetailsAction.fetch,
    fetchPrediction: fetchPlacePredictionsAction.fetch,
    setDestinationInput: predictionActions.setDestinationInput,
    setDestinationPlaceId: predictionActions.setDestinationPlaceId,
    setOriginInput: predictionActions.setOriginInput,
    setOriginPlaceId: predictionActions.setOriginPlaceId,
    setPassengersInput: predictionActions.setPassengersInput,
};

export { Widget };
// NOTE: we need withCookies HOC to get the cookies class object. useCookies hook provides a different API
export default connect(mapStateToProps, mapDispatchToProps)(withCookies(Widget));
